import type { ManualChapter } from './types'

export const coursesChapter: ManualChapter = {
  id: 'courses',
  number: 5,
  title: 'Formazione (LMS)',
  description: 'Corsi, quiz e certificazioni',
  icon: 'AcademicCapIcon',
  sections: [
    {
      id: 'catalog',
      title: 'Catalogo Corsi',
      requirement: '§2.4 Percorsi formativi completi',
      content: `
## Catalogo Corsi

Il sistema LMS (Learning Management System) offre percorsi formativi completi con video, materiali e certificazioni.

![Catalogo Corsi](courses-catalog.png)
*Il catalogo dei corsi disponibili*

### Navigare il Catalogo

1. Clicca su **"Corsi"** nel menu laterale
2. Sfoglia i corsi disponibili
3. Usa i **filtri** per trovare quello che cerchi

### Filtri Disponibili

| Filtro | Opzioni |
|--------|---------|
| **Categoria** | Per area tematica |
| **Livello** | Base, Intermedio, Avanzato |
| **Durata** | Breve, Medio, Lungo |
| **Stato** | Tutti, Gratuiti, In corso, Completati |

### Tipi di Percorso

| Percorso | Descrizione |
|----------|-------------|
| **Mini-Master** | Percorso completo con certificazione |
| **Laboratorio Etico** | Focus su casi pratici |
| **Percorso for Executives** | Per figure manageriali |
| **Corso Singolo** | Formazione su argomento specifico |
`,
      screenshots: [
        {
          id: 'courses-catalog',
          path: '/assets/manual/courses-catalog.png',
          caption: 'Catalogo dei corsi',
          alt: 'Pagina catalogo con elenco corsi'
        }
      ]
    },
    {
      id: 'course-detail',
      title: 'Dettaglio Corso',
      content: `
## Dettaglio Corso

![Dettaglio Corso](course-detail.png)
*La pagina di dettaglio di un corso*

### Informazioni Visualizzate

Nella pagina di un corso trovi:

| Sezione | Contenuto |
|---------|-----------|
| **Titolo e descrizione** | Di cosa tratta il corso |
| **Istruttore** | Chi tiene il corso |
| **Durata** | Tempo stimato per completarlo |
| **Livello** | Difficoltà (base/intermedio/avanzato) |
| **Prerequisiti** | Corsi da completare prima |
| **Programma** | Elenco di moduli e lezioni |
| **Valutazioni** | Recensioni di altri studenti |

### Programma del Corso

Il programma mostra:
- **Moduli**: Gruppi di lezioni per argomento
- **Lezioni**: Singole unità didattiche
- **Durata**: Tempo per ogni lezione
- **Tipo**: Video, PDF, Quiz, etc.
- **Stato**: Completato/Da fare
`,
      screenshots: [
        {
          id: 'course-detail',
          path: '/assets/manual/course-detail.png',
          caption: 'Pagina dettaglio corso',
          alt: 'Dettaglio corso con programma'
        }
      ]
    },
    {
      id: 'enrollment',
      title: 'Iscrizione ai Corsi',
      content: `
## Iscrizione ai Corsi

### Come Iscriversi

1. Apri la pagina del corso
2. Clicca su **"Iscriviti"** o **"Inizia il corso"**
3. Se ci sono prerequisiti, verifica di averli completati

### Tipi di Iscrizione

| Tipo | Descrizione |
|------|-------------|
| **Gratuito** | Iscrizione immediata |
| **Obbligatorio** | Assegnato dall'amministratore |
| **Con prerequisiti** | Richiede completamento altri corsi |

### Prerequisiti

Alcuni corsi richiedono di aver completato altri percorsi prima. In tal caso:
- Vedrai l'elenco dei prerequisiti
- I prerequisiti completati hanno ✅
- Quelli mancanti hanno ⚠️

### Corsi Obbligatori

I corsi assegnati dall'azienda:
- Appaiono nella tua dashboard
- Hanno una **scadenza** da rispettare
- Non possono essere rimossi
`
    },
    {
      id: 'taking-course',
      title: 'Seguire un Corso',
      requirement: '§2.4 Tracciamento avanzamento',
      content: `
## Seguire un Corso

### Navigare tra le Lezioni

1. Apri il corso dalla dashboard o dal catalogo
2. Clicca su una lezione per iniziare
3. Completa la lezione
4. Passa alla successiva

### Barra di Progresso

La barra in alto mostra:
- **% completamento** del corso
- **Lezioni completate** su totale
- **Tempo rimanente** stimato

### Tipi di Lezione

| Tipo | Contenuto | Come completare |
|------|-----------|-----------------|
| **Video** | Lezione video | Guarda fino alla fine |
| **PDF** | Documento scaricabile | Visualizza o scarica |
| **HTML** | Contenuto testuale | Leggi fino in fondo |
| **SCORM** | Contenuto interattivo | Completa le attività |
| **Quiz** | Verifica apprendimento | Supera con punteggio minimo |

### Salvare i Progressi

I progressi vengono salvati **automaticamente**:
- Puoi interrompere in qualsiasi momento
- Riprendi da dove ti eri fermato
- Il sistema ricorda la tua posizione
`
    },
    {
      id: 'video-lessons',
      title: 'Video Lezioni',
      requirement: '§2.4 Corsi SCORM, video, PDF, test interattivi',
      content: `
## Video Lezioni

### Player Video

Il player supporta video da:
- YouTube
- Vimeo
- Server interno

### Controlli Disponibili

| Controllo | Funzione |
|-----------|----------|
| ▶️ **Play/Pausa** | Avvia o ferma il video |
| 🔊 **Volume** | Regola il volume |
| ⏩ **Velocità** | 0.5x, 1x, 1.25x, 1.5x, 2x |
| 🔲 **Fullscreen** | Visualizzazione a schermo intero |
| ⏮️ **Riavvolgi** | Torna indietro di 10 secondi |
| ⏭️ **Avanti** | Vai avanti di 10 secondi |

### Completamento

Per completare una video lezione:
- Guarda almeno l'**80% del video**
- Il progresso viene salvato automaticamente
- Puoi rivedere il video quante volte vuoi
`
    },
    {
      id: 'quiz',
      title: 'Quiz e Valutazioni',
      requirement: '§2.4 Esami e certificazioni generati automaticamente',
      content: `
## Quiz e Valutazioni

### Tipi di Domande

| Tipo | Descrizione |
|------|-------------|
| **Scelta singola** | Una sola risposta corretta |
| **Scelta multipla** | Più risposte corrette |
| **Vero/Falso** | Due sole opzioni |
| **Risposta aperta** | Testo libero |

### Come Funziona un Quiz

1. Clicca su **"Inizia Quiz"**
2. Leggi attentamente ogni domanda
3. Seleziona o scrivi la risposta
4. Passa alla domanda successiva
5. Clicca **"Invia"** al termine

### Punteggio e Tentativi

| Parametro | Descrizione |
|-----------|-------------|
| **Punteggio minimo** | % per superare (es. 70%) |
| **Tempo limite** | Minuti a disposizione (se impostato) |
| **Tentativi** | Numero massimo di prove |
| **Ordine casuale** | Domande in ordine diverso ogni volta |

### Risultati

Dopo il quiz vedrai:
- **Punteggio ottenuto** (es. 85%)
- **Esito**: Superato / Non superato
- **Risposte corrette** con spiegazioni
- **Errori** commessi

### Non Hai Superato?

Se non superi il quiz:
- Attendi il tempo di attesa (se impostato)
- Rivedi il materiale del corso
- Riprova il quiz
`
    },
    {
      id: 'certificates',
      title: 'Certificati',
      requirement: '§2.4 Certificazioni generate automaticamente',
      content: `
## Certificati

Al completamento di un corso ricevi un **certificato ufficiale**.

### Come Ottenere il Certificato

1. **Completa tutte le lezioni** del corso
2. **Supera il quiz finale** (se presente)
3. Il certificato viene generato automaticamente

### Il Certificato Include

- **Nome e cognome** del partecipante
- **Titolo del corso** completato
- **Data** di completamento
- **Durata** del corso
- **Numero certificato** univoco
- **QR Code** per verifica autenticità

### Download

1. Vai su **Profilo** → **I miei certificati**
2. Trova il certificato desiderato
3. Clicca su **"Scarica PDF"**

### Condivisione

Puoi condividere il certificato:
- Scaricando il PDF
- Condividendo il link di verifica
- Aggiungendo al profilo LinkedIn (prossimamente)

> **Nota**: Il certificato è generato in PDF e può essere stampato.
`
    },
    {
      id: 'my-courses',
      title: 'I Miei Corsi',
      requirement: '§2.4 KPI formativi individuali',
      content: `
## I Miei Corsi

Tieni traccia di tutti i tuoi percorsi formativi.

### Sezioni Disponibili

| Sezione | Contenuto |
|---------|-----------|
| **In Corso** | Corsi iniziati ma non completati |
| **Completati** | Corsi terminati con successo |
| **Obbligatori** | Corsi assegnati dall'azienda |
| **Preferiti** | Corsi salvati per dopo |

### Statistiche Personali

Visualizza i tuoi KPI formativi:

- **Corsi completati** totali
- **Ore di formazione** accumulate
- **Quiz superati** / totali
- **Punteggio medio** nei quiz
- **Certificati ottenuti**

### Riprendere un Corso

Per riprendere un corso in sospeso:
1. Vai su **"I miei corsi"**
2. Trova il corso nella sezione **"In Corso"**
3. Clicca su **"Continua"**
4. Riprendi dall'ultima lezione
`
    }
  ]
}
